import { PortalApiResult, apiSlice } from "../api/apiSlice";
import { store, tabId } from "../store";

import { RootState } from "@reduxjs/toolkit/query";
import { getPatientGroup, getPatientSubGroup } from "../auth/authSlice";

export enum TaskState {
  todo = "todo",
  complete = "complete",
  deleted = "deleted",
}

export enum TaskType {
  request_to_chat = "request_to_chat",
  pending_hcw_message = "pending_hcw_message",
  followup = "followup",
  possible_interest_in_prep_or_pep = "possible_interest_in_prep_or_pep",
  interest_in_self_testing = "interest_in_self_testing",
  concern_chat_content = "concern_chat_content",
  concern_interpretation_mismatch = "concern_interpretation_mismatch",
  concern_invalid_test = "concern_invalid_test",
  concern_positive_test = "concern_positive_test",
  concern_negative_test = "concern_negative_test",
  concern_risk_other = "concern_risk_other",
  concern_risk_score = "concern_risk_score",
  concern_self_reported_hiv_positive_DEPRECATED = "concern_self_reported_hiv_positive", // Deprecated: will no longer be created by the backend
  concern_self_reported_hiv_positive_status = "concern_self_reported_hiv_positive_status",
  celosia_concern_hiv_risk_score = "celosia_concern_hiv_risk_score",
  celosia_mental_health_further_screening_required = "celosia_mental_health_further_screening_required",
  celosia_potential_sti = "celosia_potential_sti",
  mangrove_alcohol_further_screening_required = "mangrove_alcohol_further_screening_required",
  mangrove_anxiety_further_screening_required = "mangrove_anxiety_further_screening_required",
  mangrove_depression_further_screening_required = "mangrove_depression_further_screening_required",
  mangrove_tb_vulnerable = "mangrove_tb_vulnerable",
  protea_alcohol_further_screening_required = "protea_alcohol_further_screening_required",
  protea_anxiety_further_screening_required = "protea_anxiety_further_screening_required",
  protea_depression_further_screening_required = "protea_depression_further_screening_required",
  terms_accepted = "terms_accepted",
  urgent_request_to_chat = "urgent_request_to_chat",
}

export const isTaskTypeConcern = (type: TaskType) =>
  !(
    type === TaskType.request_to_chat ||
    type === TaskType.pending_hcw_message ||
    type === TaskType.followup ||
    type === TaskType.urgent_request_to_chat
  );

export const isTaskTypeChatConcern = (type: TaskType) =>
  type === TaskType.concern_chat_content;

/**
 * The concern flags carried by a task, most urgent first.
 *
 * Tasks created before AUD-13833 carry a single flag in `chat_concern_flag`;
 * newer tasks carry every concern raised while the task was open.  Both shapes
 * stay visible in the portal, so every render site goes through this helper.
 */
export const taskConcernFlags = (task: {
  chat_concern_flags?: string[] | null;
  chat_concern_flag?: string | null;
}): string[] => {
  if (task.chat_concern_flags && task.chat_concern_flags.length > 0) {
    return task.chat_concern_flags;
  }
  return task.chat_concern_flag ? [task.chat_concern_flag] : [];
};

export const isTaskTypeSelfTestConcern = (type: TaskType) =>
  type === TaskType.concern_interpretation_mismatch ||
  type === TaskType.concern_invalid_test ||
  type === TaskType.concern_positive_test ||
  type === TaskType.concern_negative_test;

export const isTaskTypeRiskConcern = (type: TaskType) =>
  type === TaskType.concern_risk_other || type === TaskType.concern_risk_score;

// NOTE: It is intentional that this function does not return true for
// TaskType.concern_self_reported_hiv_positive_DEPRECATED, because that task type
// encompasses both HIV status and HIV test result concerns.
export const isTaskTypeToFollowup = (type: TaskType) =>
  type === TaskType.concern_self_reported_hiv_positive_status ||
  isTaskTypeRiskConcern(type) ||
  type === TaskType.possible_interest_in_prep_or_pep ||
  type === TaskType.interest_in_self_testing ||
  type === TaskType.celosia_concern_hiv_risk_score ||
  type === TaskType.celosia_mental_health_further_screening_required ||
  type === TaskType.celosia_potential_sti ||
  type === TaskType.mangrove_alcohol_further_screening_required ||
  type === TaskType.mangrove_anxiety_further_screening_required ||
  type === TaskType.mangrove_depression_further_screening_required ||
  type === TaskType.mangrove_tb_vulnerable ||
  type === TaskType.protea_alcohol_further_screening_required ||
  type === TaskType.protea_anxiety_further_screening_required ||
  type === TaskType.protea_depression_further_screening_required;

export const isTaskTypeForMangrove = (type: TaskType) =>
  type === TaskType.mangrove_alcohol_further_screening_required ||
  type === TaskType.mangrove_anxiety_further_screening_required ||
  type === TaskType.mangrove_depression_further_screening_required ||
  type === TaskType.mangrove_tb_vulnerable;

export const isTaskTypeForProtea = (type: TaskType) =>
  type === TaskType.protea_alcohol_further_screening_required ||
  type === TaskType.protea_anxiety_further_screening_required ||
  type === TaskType.protea_depression_further_screening_required;

export const COMPLETED_TASKS_LIMIT = 500;
export const SEARCH_KEYWORD_FLAGGED = "flagged";

export interface TaskBasicData {
  uid: string;
  type: TaskType;
  state: TaskState;
  created: string;
  due_date: string | null;
  patient: {
    uid: string;
    name: string;
    clinicians_working_in_record: ClinicianData[];
    pending_hcw_message: boolean;
    request_to_chat: boolean;
    referral_id: string | null;
    project_specific_fields: { [key: string]: string };
  };
  chat_concern_flag: string | null;
  chat_concern_flags: string[];
  flagged_for_supervisor: boolean;
}

export interface PatientBasicData {
  uid: string;
  name: string;
  clinicians_working_in_record: ClinicianData[];
  pending_hcw_message: boolean;
  request_to_chat: boolean;
  tasks: TaskBasicData[];
}

export interface ClinicianData {
  name: string;
  picture?: string | null;
}

export interface TaskPatientData {
  uid: string;
  patientUid: string;
  type: TaskType;
  state: TaskState;
  created: string;
  title: string | null;
  due_date: string | null;
  self_test_uid: string | null;
  conversation_uid: string | null;
  chat_concern_flag: string | null;
  chat_concern_flags: string[];
  flagged_for_supervisor: boolean;
}

export interface SelfTestBasicData {
  uid: string;
  timestamp: string;
  // Request condition from Ben, e.g. "HIV"
  test_administrator_interpretation: TestInterpretation;
  image_reviewer_interpretation: TestInterpretation | null;
  test_result_source: TestResultSource;
  num_photos: number;
}

export interface SelfTestDetailData {
  uid: string;
  rdt_brand: string;
  rdt_model: string;
  rdt_condition: string;
  photos: {
    url: string;
    cv_interpretation: TestInterpretation | null;
    read_window_crop_url: string | null;
  }[];
  test_administrator_interpretation: TestInterpretation;
  image_reviewer: ClinicianData | null;
  image_reviewer_interpretation: TestInterpretation | null;
  test_result_source: TestResultSource;
}

export interface ConversationBasicData {
  uid: string;
  timestamp: string;
  type: ConversationType;
  last_recorded_message_ts: string;
}

export interface ConversationDetailData {
  uid: string;
  timestamp: string;
  type: ConversationType;
  last_recorded_message_ts: string;
  summary: {
    summary: string;
    other: string | null;
  } | null;
}

export interface PatientDisplayItem {
  task: TaskPatientData | null;
  self_test: SelfTestBasicData | null;
  conversation: ConversationBasicData | null;
  timestamp: string;
}

export enum RiskAssessmentType {
  // Predictive analytics model for HIV risk, created by Phithos
  PhithosHIVRisk = "phithos_hiv_risk",
  // HIV risk tool for celosia.  See AUD-10726 for details.
  CelosiaHIVRisk = "celosia_hiv_risk",
  // Mental health screening tool for celosia.  See AUD-10947 for details.
  CelosiaMentalHealth = "celosia_mental_health",
  // Alcohol screening tool for mangrove.
  MangroveAlcohol = "mangrove_alcohol",
  // Anxiety screening tool for mangrove.  Part of the Brief Mental Health
  // Screening Tool.  See AUD-10626 for details.
  MangroveAnxiety = "mangrove_anxiety",
  // Depression screening tool for mangrove.
  MangroveDepression = "mangrove_depression",
  // TB screening tool for mangrove.
  MangroveTB = "mangrove_tb",
}

export enum ReminderCycle {
  OneMonth = "1_month",
  TwoMonth = "2_month",
  ThreeMonth = "3_month",
  SixMonth = "6_month",
  SingleReminder = "single_reminder",
  ReminderWithFollowup = "reminder_with_followup",
}

export interface ReminderData {
  type: string;
  next_due_date: string;
  cycle: ReminderCycle;
  location: string | null;
}

export interface RiskScoreExplanation {
  description: string;
  raw_value: string | null;
  risk_score: string | null;
}

export interface RiskAssessmentData {
  type: RiskAssessmentType;
  risk_score: number;
  explanation: RiskScoreExplanation[] | null;
  risk_score_classification: string | null;
  confidence_score_classification: string | null;
  timestamp: string;
}

export interface PatientDetailData {
  uid: string;
  name: string;
  preferred_language: string;
  has_phone_number: boolean;
  displayItems: PatientDisplayItem[];
  tasks: TaskPatientData[];
  self_tests: SelfTestBasicData[];
  conversations: ConversationBasicData[];
  clinicians_working_in_record: ClinicianData[];
  pending_hcw_message: boolean;
  request_to_chat: boolean;
  reminders?: ReminderData[] | null;
  risk_assessments: RiskAssessmentData[] | null;
  referral_id: string | null;
  project_specific_fields: { [key: string]: string };
  project_specific_field_descriptions: { [key: string]: string };
}

export interface NoteEntry {
  uid: string;
  content: string;
  author: ClinicianData;
  created: string;
}

export interface PatientNotesData {
  notes: NoteEntry[];
}

export interface TaskDetailData {
  uid: string;
  type: TaskType;
  state: TaskState;
  title: string | null;
  description: string | null;
  due_date: string | null;
  created: string;
  created_by: ClinicianData | null;
  completed: string | null;
  completed_by: ClinicianData | null;
  deleted: string | null;
  deleted_by: ClinicianData | null;
  task_owner: ClinicianData | null; // currently unused
  supporting_task: string | null;
  self_test_uid: string | null;
  conversation_uid: string | null;
  notes: NoteEntry[];
  chat_concern_flag: string | null;
  chat_concern_flags: string[];
  flagged_for_supervisor: boolean;
  actions_taken: string[];
}

export enum MessageVisibility {
  visible = "visible",
  hidden_appropriate = "hidden_appropriate",
  hidden_inappropriate = "hidden_inappropriate",
}

export interface HCWMessageData {
  message_id: string;
  role: Role;
  type: MessageType;
  text: string;
  visibility: MessageVisibility;
  attachment: {
    url: string;
  } | null;
  sent_ts: string;
  hcw: ClinicianData | null;
  in_reply_to: { message_id: string } | null;
  reactions: {
    role: Role;
    emoji: string;
    hcw: ClinicianData | null;
  }[];
  delivery_status: MessageDeliveryStatus | null;
}

export enum TestInterpretation {
  positive = "positive",
  negative = "negative",
  invalid = "invalid",
  uninterpretable = "uninterpretable",
}

export enum TestResultSource {
  chatbot_ifu = "chatbot-ifu",
  chatbot_manual = "chatbot-manual",
}

export enum ConversationType {
  static = "static",
  pre_assessment = "pre-assessment",
  assessment = "assessment",
  post_assessment = "post-assessment",
  hcw = "hcw",
  ifu = "ifu",
}

const LLM_CONVERSATION_TYPES = [
  ConversationType.pre_assessment,
  ConversationType.assessment,
  ConversationType.post_assessment,
];

export enum Role {
  user = "user",
  hcw = "hcw",
  assistant = "assistant",
}

export enum MessageType {
  text = "text",
  template = "template",
  image = "image",
  audio = "audio",
  video = "video",
  //LATER: image, audio, etc.
}

export enum MessageDeliveryStatus {
  sent = "sent",
  delivered = "delivered",
  read = "read",
  failed = "failed",
}

export interface PatientProfileData {
  uid: string;
  extracted_data: {
    name: string;
    description: string;
    value: string | null;
    last_updated: string;
    history?: {
      value: string | null;
      last_updated: string;
      conversation_id: string;
    }[];
  }[];
}

export interface GetClientPhoneNumber {
  phone_number: string;
}

const apiSliceWithTagTypes = apiSlice.enhanceEndpoints({
  addTagTypes: [
    "TaskBasic",
    "PatientDetail",
    "TaskPatient",
    "TaskDetail",
    "SelfTestBasic",
    "SelfTestDetail",
    "ConversationBasic",
    "ConversationDetail",
    "PatientNoteEntries",
    "HCWMessageData",
    "PatientProfile",
  ],
});

export const getTaskLimitForState = (state: TaskState): number =>
  state === TaskState.todo ? 1000 : COMPLETED_TASKS_LIMIT;

const selectInvalidatedArguments = (
  state: RootState<any, any, "api">,
  selectedEndpointName: "getTasks" | "getPatients"
): { state: TaskState; filter: string }[] =>
  patientApiSlice.util
    .selectInvalidatedBy(state, [{ type: "TaskBasic", id: "LIST" }])
    .flatMap(({ endpointName, originalArgs }) => {
      if (endpointName === selectedEndpointName) {
        return [originalArgs];
      } else {
        return [];
      }
    });

export const patientApiSlice = apiSliceWithTagTypes.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<
      {
        taskBasicData: TaskBasicData[];
        updatedCursor: string;
        taskLimitReached: boolean;
      },
      {
        state: TaskState;
        filter: string;
      }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify(
          args.filter === "" || args.filter === SEARCH_KEYWORD_FLAGGED
            ? {
                method_id: "get_tasks",
                input: {
                  group: getPatientGroup(store.getState().auth),
                  subgroup: getPatientSubGroup(store.getState().auth),
                  state:
                    args.filter === SEARCH_KEYWORD_FLAGGED
                      ? TaskState.todo
                      : args.state,
                  order:
                    args.filter === SEARCH_KEYWORD_FLAGGED ||
                    args.state === TaskState.todo
                      ? "oldest-first"
                      : "newest-first",
                  cursor: null,
                  limit: getTaskLimitForState(args.state),
                },
              }
            : {
                method_id: "get_tasks_with_filter",
                input: {
                  group: getPatientGroup(store.getState().auth),
                  subgroup: getPatientSubGroup(store.getState().auth),
                  state: null,
                  limit: 1000,
                  filter: args.filter,
                },
              }
        ),
      }),
      providesTags: (response) => {
        return response ? [{ type: "TaskBasic", id: "LIST" }] : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<{ tasks: TaskBasicData[]; cursor: string }>,
        meta,
        args
      ) => {
        console.log("Raw result from get_tasks");
        console.log(rawResult);
        return {
          taskBasicData: rawResult.output.tasks,
          updatedCursor: rawResult.output.cursor,
          taskLimitReached:
            args.filter === "" &&
            args.state === TaskState.complete &&
            rawResult.output.tasks.length === getTaskLimitForState(args.state),
        };
      },
    }),
    getPatients: builder.query<
      {
        patientBasicData: PatientBasicData[];
        taskLimitReached: boolean;
      },
      {
        state: TaskState;
        filter: string;
      }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify(
          args.filter === "" || args.filter === SEARCH_KEYWORD_FLAGGED
            ? {
                method_id: "get_tasks",
                input: {
                  group: getPatientGroup(store.getState().auth),
                  subgroup: getPatientSubGroup(store.getState().auth),
                  state:
                    args.filter === SEARCH_KEYWORD_FLAGGED
                      ? TaskState.todo
                      : args.state,
                  order:
                    args.filter === SEARCH_KEYWORD_FLAGGED ||
                    args.state === TaskState.todo
                      ? "oldest-first"
                      : "newest-first",
                  cursor: null,
                  limit: getTaskLimitForState(args.state),
                },
              }
            : {
                method_id: "get_tasks_with_filter",
                input: {
                  group: getPatientGroup(store.getState().auth),
                  subgroup: getPatientSubGroup(store.getState().auth),
                  state: null,
                  limit: 1000,
                  filter: args.filter,
                },
              }
        ),
      }),
      providesTags: (response) => {
        return response ? [{ type: "TaskBasic", id: "LIST" }] : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<{ tasks: TaskBasicData[]; cursor: string }>,
        meta,
        args
      ) => {
        console.log("Raw result from get_tasks");
        console.log(rawResult);
        const patientBasicDataMap = rawResult.output.tasks.reduce(
          (acc, task) => {
            if (!acc[task.patient.uid]) {
              acc[task.patient.uid] = { ...task.patient, tasks: [] };
            }
            acc[task.patient.uid].tasks.push(task);
            return acc;
          },
          {} as { [key: string]: PatientBasicData }
        );
        const patientBasicData = Object.keys(patientBasicDataMap)
          .map((key) => patientBasicDataMap[key])
          .sort((a, b) =>
            args.state === TaskState.todo
              ? a.tasks[0].created.localeCompare(b.tasks[0].created)
              : b.tasks[0].created.localeCompare(a.tasks[0].created)
          );
        return {
          patientBasicData,
          taskLimitReached:
            args.filter === "" &&
            args.state === TaskState.complete &&
            rawResult.output.tasks.length === getTaskLimitForState(args.state),
        };
      },
    }),
    getPatientDetail: builder.query<PatientDetailData, { uid: string }>({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_patient_detail",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: args.uid,
          },
        }),
      }),
      providesTags: (response) => {
        return response
          ? [
              { type: "TaskPatient", id: "LIST:" + response.uid },
              { type: "PatientDetail", id: response.uid },
            ]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<PatientDetailData>,
        meta,
        args
      ) => {
        console.log("Raw result from get_patient_detail");
        console.log(rawResult);
        rawResult.output.preferred_language =
          rawResult.output.preferred_language || "en";
        rawResult.output.tasks = rawResult.output.tasks.sort((a, b) =>
          b.created.localeCompare(a.created)
        );
        rawResult.output.tasks.forEach((task) => (task.patientUid = args.uid));
        rawResult.output.displayItems = [];
        rawResult.output.tasks
          .filter((task) => !task.conversation_uid && !task.self_test_uid)
          .forEach((task) => {
            rawResult.output.displayItems.push({
              task,
              self_test: null,
              conversation: null,
              timestamp: task.created,
            });
          });
        rawResult.output.self_tests.forEach((self_test) =>
          rawResult.output.displayItems.push({
            task: null,
            self_test,
            conversation: null,
            timestamp: self_test.timestamp,
          })
        );
        rawResult.output.conversations
          .filter((conversation) =>
            LLM_CONVERSATION_TYPES.includes(conversation.type)
          )
          .forEach((conversation) =>
            rawResult.output.displayItems.push({
              task: null,
              self_test: null,
              conversation,
              timestamp: conversation.timestamp,
            })
          );
        rawResult.output.displayItems = rawResult.output.displayItems.sort(
          (a, b) => b.timestamp.localeCompare(a.timestamp)
        );
        return rawResult.output;
      },
    }),
    getPatientNotes: builder.query<PatientNotesData, { uid: string }>({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_patient_notes",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: args.uid,
            include_task_notes: true,
          },
        }),
      }),
      providesTags: (response, error, args) => {
        return response ? [{ type: "PatientNoteEntries", id: args.uid }] : [];
      },
      transformResponse: (rawResult: PortalApiResult<PatientNotesData>) => {
        console.log("Raw result from get_patient_notes");
        console.log(rawResult);
        rawResult.output.notes = rawResult.output.notes.sort((a, b) =>
          a.created.localeCompare(b.created)
        );
        return rawResult.output;
      },
    }),
    getTaskDetail: builder.query<TaskDetailData, { uid: string }>({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_task_detail",
          input: {
            group: getPatientGroup(store.getState().auth),
            task_uid: args.uid,
          },
        }),
      }),
      providesTags: (response) =>
        response ? [{ type: "TaskDetail", id: response.uid }] : [],
      transformResponse: (rawResult: PortalApiResult<TaskDetailData>) => {
        console.log("Raw result from get_task_detail");
        console.log(rawResult);
        rawResult.output.notes = rawResult.output.notes.sort((a, b) =>
          a.created.localeCompare(b.created)
        );
        return rawResult.output;
      },
    }),
    getSelfTestDetail: builder.query<SelfTestDetailData, { uid: string }>({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_self_test_detail",
          input: {
            group: getPatientGroup(store.getState().auth),
            self_test_uid: args.uid,
          },
        }),
      }),
      providesTags: (response) => {
        return response ? [{ type: "SelfTestDetail", id: response.uid }] : [];
      },
      transformResponse: (rawResult: PortalApiResult<SelfTestDetailData>) => {
        console.log("Raw result from get_self_test_detail");
        console.log(rawResult);
        return rawResult.output;
      },
    }),
    getConversationDetail: builder.query<
      ConversationDetailData,
      { uid: string }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_conversation_detail",
          input: {
            group: getPatientGroup(store.getState().auth),
            conversation_uid: args.uid,
          },
        }),
      }),
      providesTags: (response) => {
        return response
          ? [{ type: "ConversationDetail", id: response.uid }]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<ConversationDetailData>
      ) => {
        console.log("Raw result from get_conversation_detail");
        console.log(rawResult);
        return rawResult.output;
      },
    }),
    setClinicianWorkingState: builder.mutation<
      void,
      { expirationSeconds: number; patientUid: string }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "set_clinician_working_state",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: args.patientUid,
            instance_id: tabId,
            expiration_seconds: args.expirationSeconds,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        // Implement optimistic update
        const updateQueryDataForGetTasks = (state: TaskState, filter: string) =>
          patientApiSlice.util.updateQueryData(
            "getTasks",
            { state, filter },
            (draftTasks) => {
              if (draftTasks) {
                const { name, picture } = store.getState().auth;
                if (args.expirationSeconds === 0) {
                  draftTasks.taskBasicData?.forEach(
                    (taskBasic) =>
                      taskBasic.patient.uid === args.patientUid &&
                      (taskBasic.patient.clinicians_working_in_record =
                        taskBasic.patient.clinicians_working_in_record.filter(
                          (clinician) => clinician.name !== name
                        ))
                  );
                } else {
                  draftTasks.taskBasicData?.forEach((taskBasic) => {
                    if (taskBasic.patient.uid === args.patientUid) {
                      if (
                        !taskBasic.patient.clinicians_working_in_record.some(
                          (clinician) => clinician.name === name
                        )
                      ) {
                        taskBasic.patient.clinicians_working_in_record.push({
                          name,
                          picture,
                        });
                      }
                    }
                  });
                }
              }
            }
          );

        const updateQueryDataForGetPatients = (
          state: TaskState,
          filter: string
        ) =>
          patientApiSlice.util.updateQueryData(
            "getPatients",
            { state, filter },
            (draftPatients) => {
              const { name, picture } = store.getState().auth;
              draftPatients?.patientBasicData.forEach((draftPatient) => {
                if (draftPatient.uid === args.patientUid) {
                  if (args.expirationSeconds === 0) {
                    draftPatient.clinicians_working_in_record =
                      draftPatient.clinicians_working_in_record.filter(
                        (clinician) => clinician.name !== name
                      );
                  } else {
                    if (
                      !draftPatient.clinicians_working_in_record.some(
                        (clinician) => clinician.name === name
                      )
                    ) {
                      draftPatient.clinicians_working_in_record.push({
                        name,
                        picture,
                      });
                    }
                  }
                }
              });
            }
          );
        const patchResults = [
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getPatientDetail",
              { uid: args.patientUid },
              (draftPatient) => {
                if (draftPatient) {
                  const { name, picture } = store.getState().auth;
                  if (args.expirationSeconds === 0) {
                    draftPatient.clinicians_working_in_record =
                      draftPatient.clinicians_working_in_record.filter(
                        (clinician) => clinician.name !== name
                      );
                  } else if (
                    !draftPatient.clinicians_working_in_record.some(
                      (clinician) => clinician.name === name
                    )
                  ) {
                    draftPatient.clinicians_working_in_record.push({
                      name,
                      picture,
                    });
                  }
                }
              }
            )
          ),
          ...selectInvalidatedArguments(getState(), "getTasks").map(
            ({ state, filter }) =>
              dispatch(updateQueryDataForGetTasks(state, filter))
          ),
          ...selectInvalidatedArguments(getState(), "getPatients").map(
            ({ state, filter }) =>
              dispatch(updateQueryDataForGetPatients(state, filter))
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: (response, error, args) =>
        !error
          ? [
              { type: "TaskBasic", id: "LIST" },
              { type: "PatientDetail", id: args.patientUid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from set_clinician_working_state");
        console.log(rawResult);
      },
    }),
    setImageReviewerInterpretation: builder.mutation<
      void,
      {
        selfTestUid: string;
        patientUid: string;
        interpretation: TestInterpretation;
      }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "set_image_reviewer_interpretation",
          input: {
            group: getPatientGroup(store.getState().auth),
            self_test_uid: args.selfTestUid,
            image_reviewer_interpretation: args.interpretation,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic update
        const patchResults = [
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getSelfTestDetail",
              { uid: args.selfTestUid },
              (draftSelfTest) => {
                draftSelfTest.image_reviewer = {
                  name: store.getState().auth.name,
                };
                draftSelfTest.image_reviewer_interpretation =
                  args.interpretation;
              }
            )
          ),
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getPatientDetail",
              { uid: args.patientUid },
              (draftPatient) => {
                draftPatient.self_tests.find(
                  (self_test) => self_test.uid === args.selfTestUid
                )!.image_reviewer_interpretation = args.interpretation;
                const displayTask = draftPatient.displayItems.find(
                  (displayItem) =>
                    !!displayItem.self_test &&
                    displayItem.self_test.uid === args.selfTestUid
                );
                if (displayTask?.self_test) {
                  displayTask.self_test.image_reviewer_interpretation =
                    args.interpretation;
                }
              }
            )
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: (response, error, args) =>
        !error
          ? [
              { type: "SelfTestDetail", id: args.selfTestUid },
              { type: "PatientDetail", id: args.patientUid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from set_image_reviewer_interpretation");
        console.log(rawResult);
      },
    }),
    createFollowUpTask: builder.mutation<
      string,
      {
        patientUid: string;
        title: string;
        description?: string;
        dueDate?: string;
      }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "create_followup_task",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: args.patientUid,
            title: args.title,
            description: args.description || null,
            due_date: args.dueDate || null,
            supporting_task_uid: null,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic update
        const patchResult = dispatch(
          patientApiSlice.util.updateQueryData(
            "getPatientDetail",
            { uid: args.patientUid },
            (draftPatient) => {
              const task: TaskPatientData = {
                uid: "*",
                patientUid: args.patientUid,
                type: TaskType.followup,
                state: TaskState.todo,
                title: args.title,
                due_date: args.dueDate || null,
                created: new Date().toISOString(),
                self_test_uid: null,
                conversation_uid: null,
                chat_concern_flag: null,
                chat_concern_flags: [],
                flagged_for_supervisor: false,
              };
              draftPatient.tasks.unshift(task);
              draftPatient.displayItems.unshift({
                task,
                self_test: null,
                conversation: null,
                timestamp: task.created,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (response, error, args) =>
        response
          ? [
              { type: "TaskBasic", id: "LIST" },
              { type: "TaskPatient", id: "LIST:" + args.patientUid },
              { type: "TaskDetail", id: response },
              { type: "PatientDetail", id: args.patientUid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<{ uid: string }>) => {
        console.log("Raw result from create_followup_task");
        console.log(rawResult);
        return rawResult.output.uid;
      },
    }),
    completeTask: builder.mutation<
      void,
      {
        patientUid: string;
        taskUid: string;
        actionsTaken?: string[];
        noteContent?: string;
      }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "complete_task",
          input: {
            group: getPatientGroup(store.getState().auth),
            task_uid: args.taskUid,
            actions_taken: args.actionsTaken || null,
            note_content: args.noteContent || null,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic updates
        const patchResults = [
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getPatientDetail",
              { uid: args.patientUid },
              (draftPatient) => {
                const targetTask = draftPatient.tasks.find(
                  (task) => task.uid === args.taskUid
                );
                targetTask!.state = TaskState.complete;
                targetTask!.flagged_for_supervisor = false;
                const displayTask = draftPatient.displayItems.find(
                  (displayItem) =>
                    !!displayItem.task && displayItem.task.uid === args.taskUid
                );
                if (displayTask?.task) {
                  displayTask.task.state = TaskState.complete;
                  displayTask.task.flagged_for_supervisor = false;
                }
              }
            )
          ),
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getTaskDetail",
              { uid: args.taskUid },
              (draftTask) => {
                if (draftTask) {
                  draftTask.state = TaskState.complete;
                  draftTask.flagged_for_supervisor = false;
                  draftTask.actions_taken = args.actionsTaken || [];
                  draftTask.completed = new Date().toISOString();
                  draftTask.completed_by = {
                    name: store.getState().auth.name,
                  };
                }
              }
            )
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: (response, error, args) =>
        !error
          ? [
              { type: "TaskBasic", id: "LIST" },
              { type: "TaskPatient", id: "LIST:" + args.patientUid },
              { type: "TaskDetail", id: args.taskUid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from complete_task");
        console.log(rawResult);
      },
    }),
    deleteTask: builder.mutation<
      void,
      {
        patientUid: string;
        taskUid: string;
        noteContent?: string;
      }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "delete_task",
          input: {
            group: getPatientGroup(store.getState().auth),
            task_uid: args.taskUid,
            note_content: args.noteContent || null,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic updates
        const patchResults = [
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getPatientDetail",
              { uid: args.patientUid },
              (draftPatient) => {
                draftPatient.tasks.find(
                  (task) => task.uid === args.taskUid
                )!.state = TaskState.deleted;
                const displayTask = draftPatient.displayItems.find(
                  (displayItem) =>
                    !!displayItem.task && displayItem.task.uid === args.taskUid
                );
                if (displayTask?.task) {
                  displayTask.task.state = TaskState.deleted;
                }
              }
            )
          ),
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getTaskDetail",
              { uid: args.taskUid },
              (draftTask) => {
                if (draftTask) {
                  draftTask.state = TaskState.deleted;
                  draftTask.deleted = new Date().toISOString();
                  draftTask.deleted_by = {
                    name: store.getState().auth.name,
                  };
                }
              }
            )
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: (response, error, args) =>
        !error
          ? [
              { type: "TaskBasic", id: "LIST" },
              { type: "TaskPatient", id: "LIST:" + args.patientUid },
              { type: "TaskDetail", id: args.taskUid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from delete_task");
        console.log(rawResult);
      },
    }),
    addPatientNote: builder.mutation<string, { uid: string; content: string }>({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "add_patient_note",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: args.uid,
            content: args.content,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic update
        const patchResult = dispatch(
          patientApiSlice.util.updateQueryData(
            "getPatientNotes",
            { uid: args.uid },
            (draftNotes) => {
              const tempId = `temp-${Date.now()}`;
              draftNotes.notes.push({
                uid: tempId,
                created: new Date().toISOString(),
                content: args.content,
                author: {
                  name: store.getState().auth.name,
                },
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (response, error, args) =>
        response ? [{ type: "PatientNoteEntries", id: args.uid }] : [],
      transformResponse: (rawResult: PortalApiResult<{ uid: string }>) => {
        console.log("Raw result from add_patient_note");
        console.log(rawResult);
        return rawResult.output.uid;
      },
    }),
    addTaskNote: builder.mutation<
      string,
      { patientUid: string; taskUid: string; content: string }
    >({
      query: (args) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "add_task_note",
          input: {
            group: getPatientGroup(store.getState().auth),
            task_uid: args.taskUid,
            content: args.content,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic update
        const patchResult = dispatch(
          patientApiSlice.util.updateQueryData(
            "getTaskDetail",
            { uid: args.taskUid },
            (draftTask) => {
              const tempId = `temp-${Date.now()}`;
              draftTask.notes.push({
                uid: tempId,
                created: new Date().toISOString(),
                content: args.content,
                author: {
                  name: store.getState().auth.name,
                },
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (response, error, args) =>
        response
          ? [
              { type: "TaskDetail", id: args.taskUid },
              { type: "PatientNoteEntries", id: args.patientUid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<{ uid: string }>) => {
        console.log("Raw result from add_task_note");
        console.log(rawResult);
        return rawResult.output.uid;
      },
    }),
    getHCWMessages: builder.query<
      {
        messages: HCWMessageData[];
        startCursor: string | null;
        pollingCursor: string | null;
      },
      {
        uid: string;
        cursor: string | null;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_hcw_messages",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
            cursor: arg.cursor,
            limit: 1000,
          },
        }),
      }),
      keepUnusedDataFor: 0,
      providesTags: (response, error, args) => {
        return response
          ? [{ type: "HCWMessageData", id: "LIST:" + args.uid }]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<{
          messages: HCWMessageData[];
          start_cursor: string | null;
          polling_cursor: string | null;
        }>
      ) => {
        console.log("Raw result from get_hcw_messages");
        console.log(rawResult);
        return {
          messages: rawResult.output.messages,
          startCursor: rawResult.output.start_cursor,
          pollingCursor: rawResult.output.polling_cursor,
        };
      },
    }),
    pollHCWMessages: builder.query<
      {
        messages: HCWMessageData[];
        pollingCursor: string | null;
      },
      {
        uid: string;
        pollingCursor: string | null;
        timeoutSeconds: number;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "poll_hcw_messages",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
            polling_cursor: arg.pollingCursor,
            limit: 100,
            timeout_seconds: arg.timeoutSeconds,
          },
        }),
      }),
      providesTags: (response, error, args) => {
        return response
          ? [{ type: "HCWMessageData", id: "POLL:" + args.uid }]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<{
          messages: HCWMessageData[];
          polling_cursor: string | null;
        }>
      ) => {
        console.log("Raw result from poll_hcw_messages");
        console.log(rawResult);
        return {
          messages: rawResult.output.messages,
          pollingCursor: rawResult.output.polling_cursor,
        };
      },
    }),
    sendHCWMessage: builder.mutation<
      {
        messageUid: string;
      },
      {
        uid: string;
        text: string;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "send_hcw_message",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
            text: arg.text,
          },
        }),
      }),
      invalidatesTags: (response, error, args) => {
        return response
          ? [{ type: "HCWMessageData", id: "POLL:" + args.uid }]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<{
          message_id: string;
        }>
      ) => {
        console.log("Raw result from send_hcw_message");
        console.log(rawResult);
        return {
          messageUid: rawResult.output.message_id,
        };
      },
    }),
    sendHCWTemplate: builder.mutation<
      {
        messageUid: string;
      },
      {
        uid: string;
        templateName: string;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "send_hcw_template",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
            template_name: arg.templateName,
          },
        }),
      }),
      invalidatesTags: (response, error, args) => {
        return response
          ? [{ type: "HCWMessageData", id: "POLL:" + args.uid }]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<{
          message_id: string;
        }>
      ) => {
        console.log("Raw result from send_hcw_template");
        console.log(rawResult);
        return {
          messageUid: rawResult.output.message_id,
        };
      },
    }),
    setHCWMessageVisibility: builder.mutation<
      void,
      {
        patientUid: string;
        messageUid: string;
        visibility: MessageVisibility;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "set_hcw_message_visibility",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.patientUid,
            message_id: arg.messageUid,
            visibility: arg.visibility,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic update
        const patchResult = dispatch(
          patientApiSlice.util.updateQueryData(
            "getHCWMessages",
            { uid: args.patientUid, cursor: null },
            (draftMessages) => {
              draftMessages.messages
                .filter((message) => message.message_id === args.messageUid)
                .forEach((message) => (message.visibility = args.visibility));
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (response, error, args) =>
        !error
          ? [{ type: "HCWMessageData", id: "LIST:" + args.patientUid }]
          : [],
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from set_hcw_message_visibility");
        console.log(rawResult);
      },
    }),
    setTaskFlaggedForSupervisor: builder.mutation<
      void,
      {
        uid: string;
        flagged: boolean;
        patientUid: string;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "set_task_flagged_for_supervisor",
          input: {
            group: getPatientGroup(store.getState().auth),
            task_uid: arg.uid,
            flagged_for_supervisor: arg.flagged,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        // Implement optimistic update
        const updateQueryDataForGetTasks = (state: TaskState, filter: string) =>
          patientApiSlice.util.updateQueryData(
            "getTasks",
            { state, filter },
            (draftTasks) => {
              const draftTask = draftTasks.taskBasicData.find(
                (task) => task.uid === args.uid
              );
              if (draftTask) {
                draftTask.flagged_for_supervisor = args.flagged;
              }
            }
          );
        const updateQueryDataForGetPatients = (
          state: TaskState,
          filter: string
        ) =>
          patientApiSlice.util.updateQueryData(
            "getPatients",
            { state, filter },
            (draftPatients) => {
              let finishedSearch = false;
              draftPatients?.patientBasicData.some((draftPatient) => {
                const draftTask = draftPatient.tasks.find(
                  (task) => task.uid === args.uid
                );
                if (draftTask) {
                  draftTask.flagged_for_supervisor = args.flagged;
                  finishedSearch = false;
                }
                return finishedSearch;
              });
            }
          );
        const patchResults = [
          ...selectInvalidatedArguments(getState(), "getTasks").map(
            ({ state, filter }) =>
              dispatch(updateQueryDataForGetTasks(state, filter))
          ),
          ...selectInvalidatedArguments(getState(), "getPatients").map(
            ({ state, filter }) =>
              dispatch(updateQueryDataForGetPatients(state, filter))
          ),
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getPatientDetail",
              { uid: args.patientUid },
              (draftDetail) => {
                const draftDisplayItem = draftDetail.displayItems.find(
                  (displayItem) => displayItem.task?.uid === args.uid
                );
                if (draftDisplayItem) {
                  draftDisplayItem.task!.flagged_for_supervisor = args.flagged;
                }
                const draftTask = draftDetail.tasks.find(
                  (task) => task.uid === args.uid
                );
                if (draftTask) {
                  draftTask.flagged_for_supervisor = args.flagged;
                }
              }
            )
          ),
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getTaskDetail",
              { uid: args.uid },
              (draftTask) => {
                draftTask.flagged_for_supervisor = args.flagged;
              }
            )
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: (response, error, args) => {
        return !error
          ? [
              { type: "TaskBasic", id: "LIST" },
              { type: "TaskPatient", id: "LIST:" + args.patientUid },
              { type: "TaskDetail", id: args.uid },
            ]
          : [];
      },
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from set_task_flagged_for_supervisor");
        console.log(rawResult);
      },
    }),
    setProjectSpecificField: builder.mutation<
      void,
      {
        uid: string;
        fieldName: string;
        value: string;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "set_project_specific_field",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
            field_name: arg.fieldName,
            value: arg.value,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        // Implement optimistic update
        const patchResult = dispatch(
          patientApiSlice.util.updateQueryData(
            "getPatientDetail",
            { uid: args.uid },
            (draftPatient) => {
              draftPatient.project_specific_fields[args.fieldName] = args.value;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (response, error, args) =>
        !error ? [{ type: "PatientDetail", id: args.uid }] : [],
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from set_project_specific_field");
        console.log(rawResult);
      },
    }),
    setPendingHCWMessageState: builder.mutation<
      void,
      {
        uid: string;
        pendingHCWMessage: boolean;
      }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "set_pending_hcw_message_state",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
            pending_hcw_message: arg.pendingHCWMessage,
          },
        }),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        // Implement optimistic update
        const updateQueryDataForGetTasks = (state: TaskState, filter: string) =>
          patientApiSlice.util.updateQueryData(
            "getTasks",
            { state, filter },
            (draftTasks) => {
              draftTasks.taskBasicData
                .filter((task) => task.patient.uid === args.uid)
                .forEach(
                  (task) =>
                    (task.patient.pending_hcw_message = args.pendingHCWMessage)
                );
            }
          );
        const updateQueryDataForGetPatients = (
          state: TaskState,
          filter: string
        ) =>
          patientApiSlice.util.updateQueryData(
            "getPatients",
            { state, filter },
            (draftPatients) => {
              const draftPatient = draftPatients?.patientBasicData.find(
                (patient) => patient.uid === args.uid
              );
              if (draftPatient) {
                draftPatient.pending_hcw_message = args.pendingHCWMessage;
              }
            }
          );
        const patchResults = [
          ...selectInvalidatedArguments(getState(), "getTasks").map(
            ({ state, filter }) =>
              dispatch(updateQueryDataForGetTasks(state, filter))
          ),
          ...selectInvalidatedArguments(getState(), "getPatients").map(
            ({ state, filter }) =>
              dispatch(updateQueryDataForGetPatients(state, filter))
          ),
          dispatch(
            patientApiSlice.util.updateQueryData(
              "getPatientDetail",
              { uid: args.uid },
              (draftPatient) => {
                draftPatient.pending_hcw_message = args.pendingHCWMessage;
              }
            )
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: (response, error, args) => {
        return !error
          ? [
              { type: "TaskBasic", id: "LIST" },
              { type: "TaskPatient", id: "LIST:" + args.uid },
            ]
          : [];
      },
      transformResponse: (rawResult: PortalApiResult<void>) => {
        console.log("Raw result from set_pending_hcw_message_state");
        console.log(rawResult);
      },
    }),
    getPatientProfile: builder.query<PatientProfileData, { uid: string }>({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_patient_profile",
          input: {
            group: getPatientGroup(store.getState().auth),
            patient_uid: arg.uid,
          },
        }),
      }),
      providesTags: (response, error, args) => {
        return response
          ? [{ type: "PatientProfile", id: "LIST:" + args.uid }]
          : [];
      },
      transformResponse: (
        rawResult: PortalApiResult<PatientProfileData>,
        meta,
        args
      ) => {
        console.log("Raw result from get_patient_profile");
        console.log(rawResult);
        return rawResult.output;
      },
    }),
    getClientPhoneNumber: builder.query<
      GetClientPhoneNumber,
      { uid: string; reason: string }
    >({
      query: (arg) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "request_phone_number",
          input: {
            group: getPatientGroup(store.getState().auth),
            uid: arg.uid,
            reason: arg.reason,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<GetClientPhoneNumber>) => {
        console.log("Raw result from get_client_phone_number");
        console.log(rawResult);
        return rawResult.output;
      },
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetPatientsQuery,
  useGetPatientDetailQuery,
  useLazyGetPatientNotesQuery,
  useLazyGetTaskDetailQuery,
  useLazyGetSelfTestDetailQuery,
  useLazyGetConversationDetailQuery,
  useSetClinicianWorkingStateMutation,
  useSetImageReviewerInterpretationMutation,
  useCreateFollowUpTaskMutation,
  useCompleteTaskMutation,
  useDeleteTaskMutation,
  useAddPatientNoteMutation,
  useAddTaskNoteMutation,
  useGetHCWMessagesQuery,
  useLazyPollHCWMessagesQuery,
  useSendHCWMessageMutation,
  useSendHCWTemplateMutation,
  useSetHCWMessageVisibilityMutation,
  useSetTaskFlaggedForSupervisorMutation,
  useSetProjectSpecificFieldMutation,
  useSetPendingHCWMessageStateMutation,
  useLazyGetPatientProfileQuery,
  useLazyGetClientPhoneNumberQuery,
} = patientApiSlice;
